import { useState } from "react";
import { BtnPrimary, Card, Pill } from "../components/ui";
import { S } from "../theme";

const BLOG_POSTS = [
  {
    id: "bpc-157-complete-guide",
    title: "BPC-157: The Complete Guide for 2026",
    excerpt: "Everything you need to know about Body Protection Compound 157 — the peptide everyone's calling the 'Wolverine compound.' Mechanism, dosing, stacking, and what the research actually says.",
    category: "Peptide Guides",
    readTime: "8 min read",
    date: "May 18, 2026",
    color: "#2d6a4f",
    content: `BPC-157, short for Body Protection Compound 157, is a synthetic peptide derived from a protective protein found in human gastric juice. Over the past five years it has become one of the most discussed peptides in biohacking communities, in athletic recovery circles, and increasingly in mainstream wellness. The hype isn't unfounded — but it isn't fully validated either. Here is what we actually know.

## What BPC-157 does

BPC-157 is a 15 amino acid sequence that appears to promote angiogenesis, which is the formation of new blood vessels. It also seems to upregulate growth hormone receptors and accelerate the migration of fibroblasts to sites of injury. In animal studies, it has shown remarkable effects on tendon, ligament, muscle, and gut lining repair. Importantly, BPC-157 also appears to protect the gastrointestinal system — which is consistent with its origin in stomach acid.

## What the research actually shows

The vast majority of BPC-157 research has been conducted in rats and rabbits. In those animals, the effects are consistent and impressive: faster wound healing, reduced inflammation, and accelerated tendon repair. Human research is far more limited. There are no large randomized controlled trials in humans, and most clinical use is based on extrapolation from animal data and clinical experience from physicians who prescribe it via compounding pharmacies.

This is the honest situation: the mechanism is plausible, the animal data is strong, and anecdotal human evidence is widespread. But until human trials are run, claims should be made carefully.

## Common dosing protocols

Most published protocols suggest 250 to 500 micrograms per day, typically administered subcutaneously near the injury site. Some users prefer intramuscular injection for localized tendinopathy. Cycles typically run 4 to 12 weeks. The peptide has a short half-life, so daily dosing is standard.

## Stacking with TB-500

The "Wolverine Stack" combines BPC-157 with TB-500 (Thymosin Beta-4). The rationale is that BPC-157 acts locally on tissue repair while TB-500 acts systemically on cell migration and recovery. Many users report that the combination works faster than either alone, especially for chronic tendinopathies. TB-500 is typically dosed at 2 to 2.5 mg twice per week.

## The regulatory picture

BPC-157 sits in a legal gray area in the United States. It is not FDA approved for any use, and as of early 2026 the FDA has discouraged compounding pharmacies from preparing it. However, the regulatory landscape is shifting — recent announcements suggest BPC-157 may move back into legal compounding status under physician prescription. Track the FDA Updates page in TrackMyPeptide to stay current.

## Bottom line

BPC-157 is one of the most promising recovery peptides, with strong animal data and widespread clinical use. The lack of human trials is a real limitation, and the legal landscape is fluid. Anyone considering BPC-157 should work with a licensed physician, source from a reputable compounding pharmacy, and track their biomarkers and subjective response to determine if it's actually working for them.`
  },
  {
    id: "semaglutide-vs-tirzepatide",
    title: "Semaglutide vs Tirzepatide: Which GLP-1 Is Right for You?",
    excerpt: "Ozempic vs Mounjaro. Wegovy vs Zepbound. They're both blockbuster peptides — but they work differently. Here's the honest comparison.",
    category: "GLP-1 & Weight Loss",
    readTime: "7 min read",
    date: "May 15, 2026",
    color: "#0284c7",
    content: `The GLP-1 revolution has transformed how we think about weight loss and metabolic health. Two compounds dominate the conversation: semaglutide (sold as Ozempic for diabetes and Wegovy for weight loss) and tirzepatide (sold as Mounjaro and Zepbound). They are often discussed interchangeably, but they work differently and have different clinical profiles.

## How they work

Semaglutide is a GLP-1 receptor agonist. It mimics the hormone GLP-1, which is naturally released after eating. GLP-1 reduces appetite, slows gastric emptying, improves insulin sensitivity, and reduces glucagon release. Semaglutide has a long half-life, allowing for once-weekly dosing.

Tirzepatide is a dual GIP and GLP-1 receptor agonist. In addition to mimicking GLP-1, it also activates the GIP receptor, which appears to enhance insulin sensitivity and may modulate fat metabolism in ways that GLP-1 alone does not. This dual action is why tirzepatide tends to produce greater weight loss than semaglutide in head-to-head trials.

## What the trials show

In the SURPASS-2 trial, tirzepatide produced significantly greater weight loss than semaglutide. At the highest dose, tirzepatide users lost roughly 22 percent of body weight on average, compared to about 15 percent for semaglutide. For type 2 diabetes management, both reduce A1C effectively, but tirzepatide tends to produce slightly greater reductions.

## Side effects

Both medications share a similar side effect profile: nausea, constipation or diarrhea, occasional vomiting, and reduced appetite (which is generally considered beneficial). The side effects tend to be worst during the titration period, which is why both drugs are started at low doses and gradually increased over weeks.

Tirzepatide users sometimes report more pronounced gastrointestinal side effects in the early weeks, possibly because of the additional GIP activity. However, individual response varies enormously.

## Practical considerations

Dosing schedules are similar — both are once-weekly subcutaneous injections, both require titration, and both work best when paired with attention to nutrition and movement. Cost varies considerably depending on insurance coverage and whether you're sourcing branded medication or working with a compounding pharmacy.

## Which is right for you?

There is no universal answer. Tirzepatide tends to produce more weight loss but may have stronger gastrointestinal effects. Semaglutide has a longer track record and a slightly more predictable side effect profile. Both require medical supervision and should not be used without a prescription from a licensed physician.

If you are tracking GLP-1 therapy, log your weekly dose, weight, A1C if available, and subjective hunger levels. Patterns emerge over weeks, not days.`
  },
  {
    id: "wolverine-stack-explained",
    title: "The Wolverine Stack: BPC-157 + TB-500 Explained",
    excerpt: "Why athletes and biohackers swear by this combo for chronic tendon, ligament, and joint issues that won't heal any other way.",
    category: "Recovery Protocols",
    readTime: "6 min read",
    date: "May 12, 2026",
    color: "#6b4fbb",
    content: `The "Wolverine Stack" is the unofficial name for combining BPC-157 and TB-500. It's named after the Marvel character known for rapid healing, and the comparison is intentional — users report that the combination accelerates recovery from injuries that have been chronic for months or even years.

## The mechanism behind the stack

BPC-157 works primarily on tissue repair at the local level. It promotes new blood vessel formation, accelerates fibroblast migration, and protects damaged tissue from further inflammation. When injected near an injury, it tends to work locally.

TB-500, or Thymosin Beta-4, works systemically. It promotes cell migration and tissue repair throughout the body, not just at the injection site. It also has potent anti-inflammatory effects and appears to upregulate actin, which is essential for cellular movement and repair.

The theory behind stacking them is that BPC-157 handles localized repair while TB-500 supports systemic recovery — and together they accelerate the healing process more than either alone.

## Common protocols

A typical Wolverine Stack protocol looks like this. BPC-157 at 250 to 500 micrograms per day, subcutaneously, ideally near the affected area. TB-500 at 2 to 2.5 mg twice per week for a loading phase of 4 to 6 weeks, then 2 to 2.5 mg once every 1 to 2 weeks as maintenance.

Cycles often run 6 to 12 weeks. Many users report that the first noticeable changes appear around week 3 to 4, with more substantial improvements by week 6 to 8.

## Who tends to benefit

The Wolverine Stack tends to be most useful for chronic tendinopathies (Achilles, patellar, elbow), ligament injuries that haven't healed with rest and rehab, post-surgical recovery, and stubborn joint issues. It is not a magic bullet — proper physical therapy and load management are still essential. The peptides accelerate the body's natural repair processes, but they cannot replace mechanical rehabilitation.

## What to expect

Results are usually gradual, not dramatic. The first few weeks may produce no obvious changes. Around week 3 to 4, many users notice subtle improvements — less pain on stairs, better range of motion, less morning stiffness. By week 6 to 8, more substantial changes become apparent.

If you don't see any improvement after 8 weeks, the protocol may not be addressing your specific issue, or the dose may need adjustment.

## Tracking the stack

Log your daily BPC-157 dose, your weekly TB-500 dose, and a daily pain or function score (a 1 to 10 scale works fine). Take photos or video of your range of motion every two weeks. Compare side by side at week 8. Subjective improvement is often more apparent than you remember.`
  },
  {
    id: "is-bpc-157-legal",
    title: "Is BPC-157 Legal in 2026? The Real Answer",
    excerpt: "The regulatory picture for BPC-157 is shifting fast. Here's where things actually stand right now and what's coming.",
    category: "FDA & Legal",
    readTime: "5 min read",
    date: "May 10, 2026",
    color: "#dc2626",
    content: `The legal status of BPC-157 in the United States is one of the most confusing topics in the peptide community. Information is contradictory, the landscape is shifting, and most online discussions are out of date within months. Here is the current picture as of mid-2026.

## What "legal" actually means

BPC-157 is not a controlled substance. Possessing it is not a criminal offense. The legal question is really about how it can be sold, manufactured, and prescribed.

## The FDA Category 2 situation

In late 2023, the FDA placed BPC-157 on what is informally known as the "Category 2" bulk substances list. This effectively prohibited 503A compounding pharmacies from preparing BPC-157 for patient prescriptions. The reasoning was a lack of safety data and concerns about quality control.

This created a paradox: BPC-157 was widely used by physicians and patients, but the standard legal pathway to obtain it (a compounding pharmacy under physician prescription) was closed. The result was that demand shifted toward research chemical vendors, which are technically legal to sell but not legal to label for human use.

## The 2026 shift

In February 2026, Health and Human Services Secretary Robert F. Kennedy Jr. announced on a podcast that approximately 14 of the 19 restricted peptides — including BPC-157, TB-500, CJC-1295, and Ipamorelin — were expected to return to legal compounding status under physician prescription.

As of the time of this writing, formal rulemaking is still in progress. The announcement signals intent, but specific peptides will likely be addressed individually, and the timeline is unclear. Compounding pharmacies are watching carefully but most have not yet resumed BPC-157 preparation.

## What this means for you

If you have an existing relationship with a physician who is following the regulatory situation closely, this is a good time to discuss whether a compounding prescription will become available. If you are sourcing from research chemical vendors, understand that this is not the same legal pathway as a physician prescription, and quality varies enormously between vendors.

Track regulatory developments closely. The TrackMyPeptide FDA Updates page pulls in current information automatically. The situation can shift in either direction with little notice.

## The bigger picture

The peptide regulatory landscape is in transition. The political environment under the current administration appears more receptive to peptide therapies than the previous administration. However, the FDA's role is to regulate based on safety and efficacy data, and the underlying lack of large human trials remains a real barrier. Expect continued movement on this issue throughout 2026 and 2027.`
  },
  {
    id: "ghk-cu-for-skin",
    title: "GHK-Cu for Skin: Why Women in Wellness Are Switching to Copper Peptides",
    excerpt: "Botox is expensive, retinol takes months. Here's why GHK-Cu has quietly become the favorite peptide of skincare-focused biohackers.",
    category: "Anti-Aging",
    readTime: "6 min read",
    date: "May 8, 2026",
    color: "#0891b2",
    content: `GHK-Cu, or copper peptide GHK, is one of the more fascinating molecules in the peptide world. It is a naturally occurring tripeptide (three amino acids: glycine, histidine, and lysine) bound to a copper ion. It exists in your body — but levels decline sharply with age, dropping by more than half between your twenties and your sixties.

That decline correlates closely with visible skin aging, which is why GHK-Cu has become one of the most studied peptides for skin regeneration.

## What GHK-Cu actually does

GHK-Cu has been studied extensively for its effects on the skin. The research shows several mechanisms. It stimulates collagen and elastin synthesis. It improves skin firmness and elasticity. It accelerates wound healing. It modulates inflammation. And it has antioxidant effects that protect against UV damage.

What is striking about GHK-Cu is that it appears to "reset" gene expression in older skin cells toward a more youthful pattern. This is not just superficial — it is a deeper cellular effect.

## How it is used

GHK-Cu can be used topically (as a serum or cream) or injected subcutaneously. Topical use is popular for skincare and is generally safer because it bypasses any concerns about systemic effects. Subcutaneous injection produces more pronounced systemic effects and is more popular among biohackers focused on overall anti-aging rather than just skin appearance.

For topical use, look for serums with 0.1 to 2 percent GHK-Cu. Apply morning and night to clean skin. Results are gradual — most users notice improved texture and tone after 4 to 8 weeks.

For injection, common protocols are 1 to 2 mg per day subcutaneously, cycled 4 to 8 weeks on and 2 to 4 weeks off.

## Why women in wellness are noticing

The aesthetic medicine industry has been dominated by injectables (botulinum toxin, hyaluronic acid fillers) for the past decade. These are effective but expensive, require ongoing maintenance, and have a quick onset of action. GHK-Cu represents a different approach — it is slower acting but works with the body's natural repair mechanisms rather than masking signs of aging.

For women in wellness who are already comfortable with subcutaneous injections (often because they're also using GLP-1s or other peptides), GHK-Cu is a natural addition. It pairs well with semaglutide or tirzepatide because rapid weight loss can sometimes lead to skin laxity, and GHK-Cu may help maintain collagen integrity during that process.

## Safety and side effects

GHK-Cu has an excellent safety profile in the published research. The most common side effects are mild redness or irritation at the injection or application site. Because it contains copper, users with copper sensitivity or Wilson's disease should avoid it.

## Stacking

Common pairings include GHK-Cu plus Epithalon for longevity-focused protocols, GHK-Cu plus semaglutide for women managing weight loss, and GHK-Cu topical plus retinol for skincare maximalism.

As always, work with a physician, source from a reputable supplier, and track your results over weeks rather than days.`
  },
  {
    id: "reconstitution-guide",
    title: "How to Reconstitute Peptides Without Screwing Up the Math",
    excerpt: "Reconstitution math is where most peptide protocols fail. Here's the simple framework so you never miscalculate a dose again.",
    category: "Education",
    readTime: "7 min read",
    date: "May 5, 2026",
    color: "#e76f3b",
    content: `Reconstitution is the process of mixing a lyophilized (freeze-dried) peptide with bacteriostatic water to create an injectable solution. It is also where most peptide users make mistakes — and dosing errors can mean wasting expensive peptide, getting weaker effects than expected, or in rare cases, taking much more than intended.

Here is the framework that prevents almost all reconstitution errors.

## The three numbers you need to know

To reconstitute a peptide, you need three pieces of information.

First, the total amount of peptide in the vial, measured in milligrams or micrograms.

Second, the amount of bacteriostatic water you'll add to the vial, measured in milliliters.

Third, the dose you want per injection, measured in micrograms or milligrams.

That's it. From those three numbers, you can calculate everything.

## The simple formula

Concentration equals total amount divided by volume of water.

Volume per dose equals desired dose divided by concentration.

If you understand those two equations, you can dose any peptide accurately.

## Worked example: BPC-157

You have a 5 mg vial of BPC-157. You add 2 mL of bacteriostatic water. You want to dose 250 micrograms per injection.

Step one: convert everything to consistent units. 5 mg is 5000 micrograms.

Step two: calculate concentration. 5000 micrograms divided by 2 mL equals 2500 micrograms per mL.

Step three: calculate volume per dose. 250 micrograms divided by 2500 micrograms per mL equals 0.1 mL.

So you would draw 0.1 mL into your syringe to get a 250 microgram dose. On a U-100 insulin syringe, 0.1 mL is the 10 unit mark.

## The insulin syringe unit conversion

Most peptide users inject with U-100 insulin syringes, which are marked in "units" rather than mL. The conversion is simple: 100 units equals 1 mL. So 10 units equals 0.1 mL, 25 units equals 0.25 mL, 50 units equals 0.5 mL.

## Choosing how much water to add

There is no single right answer for how much water to add. The choice affects how much volume you'll inject per dose. More water means larger injection volume per dose, which can be helpful if you have trouble seeing tiny amounts on the syringe. Less water means smaller volumes per dose, which can mean less injection discomfort but harder accuracy.

For most peptides, 2 to 5 mL of bacteriostatic water per vial is reasonable. The TrackMyPeptide app has a built-in reconstitution calculator if you want to skip the math entirely.

## Storage after reconstitution

Once reconstituted, most peptides should be refrigerated and used within 30 days. Some peptides (like BPC-157) are more stable; others (like CJC-1295 without DAC) degrade faster. When in doubt, follow the guidance from your compounding pharmacy or supplier.

## Common mistakes

The most common mistake is unit confusion — milligrams versus micrograms. A 5 mg vial is 5000 micrograms. If you accidentally calculate as if it were 5 micrograms, you'll inject 1000 times more than intended.

The second most common mistake is using the wrong syringe markings. Some users assume the units on a syringe equal the milligrams of peptide. They do not. Units measure volume, not peptide content.

When in doubt, double-check your math, or use the reconstitution calculator in the app.`
  },
  {
    id: "peptides-for-sleep",
    title: "Best Peptides for Sleep: What Actually Works",
    excerpt: "DSIP, Ipamorelin, CJC-1295, and the underrated peptides nobody talks about. Here's what the research and the community say about peptide-assisted sleep.",
    category: "Performance",
    readTime: "6 min read",
    date: "May 3, 2026",
    color: "#7c3aed",
    content: `Sleep is the single highest-leverage variable for recovery, cognitive performance, and overall health. It is also one of the most common complaints in the biohacking community. Several peptides have shown effects on sleep architecture — but the field is full of overstated claims and weak evidence. Here is what is actually worth considering.

## Ipamorelin and CJC-1295

These are growth hormone secretagogues. They stimulate the natural pulsatile release of growth hormone, which peaks during deep sleep in healthy young adults. As you age, GH release declines, which contributes to less restorative sleep.

Most users who run Ipamorelin and CJC-1295 report improved sleep quality within the first 1 to 2 weeks. Deep sleep duration increases on wearables (Oura, WHOOP). Heart rate variability typically improves over 3 to 6 weeks. Users wake feeling more refreshed.

Typical protocol: 100 to 200 micrograms of each, subcutaneous, 30 to 60 minutes before bed, 5 days per week.

The reason this works for sleep is straightforward: GH release amplifies deep sleep, and deep sleep is when the body does the most repair. If you're already sleep-deprived, peptides won't compensate for not being in bed. But if you're getting 7 to 9 hours and want better quality, this stack has strong anecdotal support.

## DSIP (Delta Sleep-Inducing Peptide)

DSIP is a nonapeptide that was originally isolated from the brains of rabbits in slow-wave sleep. The name suggests sleep induction, and the early research seemed promising. However, more recent research has not consistently shown a strong sleep effect in humans, and the peptide is not widely used in practice.

DSIP may still have a role for specific cases — particularly for users with stress-driven sleep disruption — but it is not a universal sleep aid.

## Pinealon

Pinealon is a tripeptide developed by the same Russian research team behind Epithalon. It targets the central nervous system and pineal gland. Early animal research and some human work suggests it may improve sleep quality, support natural melatonin rhythms, and have neuroprotective effects.

Typical protocol: 5 to 10 mg per day subcutaneously, in courses of 10 to 20 days, repeated 1 to 2 times per year.

## Selank

Selank is primarily known as an anxiolytic and nootropic peptide, but it has secondary effects on sleep — particularly for users whose sleep issues are driven by anxiety or racing thoughts. It is administered intranasally.

Typical protocol: 250 to 300 micrograms 1 to 3 times per day, intranasal, in 2 to 4 week courses.

## What probably won't help

Several peptides marketed for sleep do not have strong evidence behind that claim. Be cautious about products positioned primarily on sleep that don't have well-documented mechanisms.

## The bigger picture

Peptides are at best a complement to good sleep hygiene, not a replacement. The biggest gains in sleep usually come from going to bed at the same time each night, keeping the bedroom cold and dark, avoiding screens for 1 to 2 hours before bed, and managing caffeine and alcohol intake. Once those are dialed in, peptides can produce additional improvement.

Track your sleep with a wearable if you can. Subjective improvement is real but easy to over-attribute. Objective data over 4 to 6 weeks tells the real story.`
  },
  {
    id: "peptides-for-women",
    title: "Peptides for Women: A Complete Beginner's Guide",
    excerpt: "Most peptide content is written for men. Here's the guide for women — what works, what to watch for, and how to start safely.",
    category: "Women's Health",
    readTime: "9 min read",
    date: "April 30, 2026",
    color: "#be185d",
    content: `Most peptide content is written for and by men. The community on Reddit, the influencers on TikTok, the protocols passed around in gyms — they are largely male-centric. Women face a different set of considerations, both physiological and practical. This guide addresses peptides specifically through a women's lens.

## Why dosing is different

Women generally have lower body mass, different fat distribution, different hormone profiles, and different baseline biomarkers than men. Peptide doses derived from male protocols are often too high for women. A protocol that produces a desired effect at 250 micrograms in a 200-pound male might produce excessive side effects at the same dose in a 140-pound female.

The general rule is to start at 60 to 75 percent of the male dose and titrate up if needed.

## Hormonal considerations

Women's hormonal cycles affect how they respond to many peptides. GH-stimulating peptides (Ipamorelin, CJC-1295) may have more variable effects across the menstrual cycle. Peptides affecting metabolism (semaglutide, tirzepatide, MOTS-c) may have different effects in the follicular versus luteal phase.

Women on hormonal birth control or hormone replacement therapy should discuss peptide use with their physician, as some interactions are possible.

Women who are pregnant or breastfeeding should not use peptides except under direct medical supervision.

## Peptides commonly used by women

GLP-1 agonists (semaglutide, tirzepatide) are widely used by women for weight management. The evidence base is strong, the side effect profile is well documented, and the supervision pathway is clear.

GHK-Cu is popular with women focused on skin health, both topically and via subcutaneous injection. Side effects are minimal.

Ipamorelin and CJC-1295 are used by women for sleep, recovery, and body composition. Doses are typically on the lower end of published protocols.

Selank and Semax are used by women for cognitive performance and anxiety management. Intranasal administration is convenient.

BPC-157 is used by women for joint and tendon issues, particularly common in dancers, runners, and CrossFit athletes.

## Peptides to be cautious with

Some peptides have more pronounced androgenic or virilizing effects and are not generally recommended for women, particularly those of reproductive age. PT-141 (bremelanotide) for sexual function is widely used by women and is FDA approved for women under the brand name Vyleesi, so it is an exception.

Aggressive GH protocols (high-dose GHRP-2, GHRP-6, hexarelin) tend to produce more side effects in women and are usually not necessary at high doses.

## A starter protocol for a woman new to peptides

If you are completely new and want to start somewhere relatively well-studied and lower-risk, a reasonable starter approach might be GHK-Cu topical applied morning and night, plus tracking biomarkers (sleep, HRV, body composition, skin photos) for 60 days before adding anything else.

If your goal is weight management, work with a physician on a GLP-1 protocol — that is the most evidence-based path.

If your goal is recovery from a specific injury, BPC-157 alone at modest doses is a reasonable starting point.

Resist the urge to start multiple peptides at once. You learn nothing from a stack of five peptides started simultaneously because you cannot tell what is working.

## Tracking matters more for women

Because women's responses are more variable across the cycle, tracking is even more important. Log subjective measures (energy, mood, sleep quality, recovery), objective measures (weight, body fat if accessible, HRV), and any side effects. Patterns emerge over 60 to 90 days that wouldn't be obvious in the first few weeks.

The TrackMyPeptide community has many women sharing their protocols and results. It is one of the best places to learn from peers who have already navigated the same questions.`
  },
  {
    id: "ipamorelin-cjc-stack",
    title: "Ipamorelin + CJC-1295: The Most Popular Stack for a Reason",
    excerpt: "This combination has dominated growth hormone protocols for a decade. Here's what makes it work, who it's for, and how to run it properly.",
    category: "GH Peptides",
    readTime: "7 min read",
    date: "April 27, 2026",
    color: "#c0562a",
    content: `Ipamorelin combined with CJC-1295 is the most widely used peptide stack for growth hormone optimization. It has a decade of clinical use behind it, an excellent safety profile, and produces noticeable effects in most users within 2 to 4 weeks. Here is why it works and how to run it.

## The mechanism

Growth hormone is released in pulses, primarily during deep sleep, throughout your life. As you age, the size and frequency of those pulses decrease, contributing to many of the changes associated with aging — slower recovery, more body fat, less lean muscle, worse sleep.

Ipamorelin is a selective growth hormone secretagogue. It binds to the ghrelin receptor and triggers GH release. Unlike older GHRPs, it is highly selective and does not significantly raise cortisol or prolactin.

CJC-1295 is a GHRH (growth hormone-releasing hormone) analog. It binds to the GHRH receptor on the pituitary, also triggering GH release, but through a different mechanism.

When used together, the two peptides produce a larger GH pulse than either alone. This is the rationale for the stack: synergistic action through two complementary pathways.

## CJC-1295 with or without DAC

CJC-1295 comes in two forms. The DAC version (Drug Affinity Complex) extends the half-life dramatically — DAC versions stay in the bloodstream for days, producing a more sustained GH elevation. The non-DAC version (sometimes called Mod GRF 1-29) has a much shorter half-life and produces a more physiological pulsatile pattern when combined with Ipamorelin.

Most current protocols favor non-DAC CJC-1295 because it produces a more natural GH pulse pattern. DAC versions are still used but tend to produce more side effects (water retention, hand tingling) at higher doses.

## Common dosing

A typical protocol is 100 to 200 micrograms of CJC-1295 (non-DAC) plus 100 to 200 micrograms of Ipamorelin, combined in one subcutaneous injection, administered 30 to 60 minutes before bedtime. Some users dose 2 to 3 times per day for more aggressive protocols, but once before bed is the most common pattern.

Cycles run 8 to 12 weeks, followed by 2 to 4 weeks off to prevent receptor desensitization.

## What to expect

In the first 1 to 2 weeks, users typically notice deeper sleep and feeling more rested in the morning. Around weeks 3 to 4, recovery from training improves and body composition starts to change. By weeks 6 to 8, more substantial changes appear — reduced body fat (especially visceral fat), improved skin appearance, sometimes hair improvements, and better mood and energy.

The effects build over time. The peptides don't dump GH all at once — they restore a more youthful pulsatile pattern, and the body adapts to that over weeks.

## Biomarkers to track

If you have access to blood work, IGF-1 is the key biomarker. IGF-1 is downstream of GH and stable enough to measure. A baseline reading before starting, followed by another at 8 weeks, will tell you whether your protocol is actually elevating GH. Most users see IGF-1 rise by 30 to 80 percent on this stack.

If you don't have access to blood work, subjective measures still tell a story. Track sleep quality, morning energy, recovery from training, and any visible changes in body composition over 8 to 12 weeks.

## Side effects

Side effects are usually mild. Flushing or warmth in the face for 5 to 10 minutes after injection is common in the first few days. Some users report a tingling sensation in the hands during the first week. Excessive water retention is uncommon at standard doses.

If side effects persist or are pronounced, reduce the dose by 25 to 50 percent. Most users tolerate the stack well.

## When to cycle off

Receptor desensitization is real but slow. Most users can run 8 to 12 week cycles without significant tolerance development. Beyond 12 weeks of continuous use, returns diminish. A 2 to 4 week break restores sensitivity.

If you are running this stack as part of a longer-term protocol, the typical pattern is 12 weeks on, 4 weeks off, repeated as desired.`
  },
  {
    id: "fda-update-2026",
    title: "What the 2026 FDA Peptide Shift Actually Means",
    excerpt: "RFK Jr.'s February announcement about returning 14 peptides to compounding status — what it actually does, what it doesn't, and what's next.",
    category: "FDA & Legal",
    readTime: "5 min read",
    date: "April 24, 2026",
    color: "#dc2626",
    content: `In February 2026, Health and Human Services Secretary Robert F. Kennedy Jr. announced on a podcast that approximately 14 of the 19 previously restricted peptides — including BPC-157, TB-500, CJC-1295, Ipamorelin, Sermorelin, and others — were expected to return to legal compounding status under physician prescription.

This was widely reported as the biggest regulatory shift for peptides in years. But the actual situation is more nuanced. Here is what the announcement does, what it doesn't, and what to watch for next.

## What the announcement does

It signals intent at the highest level of the Department of Health and Human Services to revisit the FDA's previous restrictions on peptide compounding. It puts the issue on the political agenda. It gives compounding pharmacies and physicians a basis for advocacy. And it changes the tone of the conversation — peptides are now being discussed in mainstream political and media contexts.

## What the announcement doesn't do

It does not immediately change FDA policy. The FDA operates through formal rulemaking processes that take months and sometimes years. Specific peptides will likely need to be individually evaluated.

It does not legalize possession or use of peptides that weren't already legally possessable.

It does not change anything about research chemical vendors, which operate in a separate (and generally unregulated) market.

It does not change the underlying clinical evidence base for these peptides, which remains thin compared to FDA-approved drugs.

## Which peptides are affected

The 14 peptides mentioned (though not all officially confirmed) include BPC-157, TB-500, CJC-1295, Ipamorelin, Sermorelin, PT-141, Epithalon, GHK-Cu, Selank, Semax, MOTS-c, AOD-9604, Tesamorelin, and Thymosin Alpha-1.

Some of these (PT-141, Tesamorelin, Thymosin Alpha-1) are already FDA-approved for specific indications. Others are not.

Tirzepatide and semaglutide were not part of this announcement — those are still under separate ongoing rulemaking about GLP-1 compounding.

## What this means practically

If you have an existing relationship with a physician who prescribes peptides through a compounding pharmacy, the legal pathway may become smoother over the coming months.

If you are sourcing from research chemical vendors, this announcement does not change the legal status of that pathway, but it may shift the market dynamics. Physician-supervised compounding tends to come with better quality control and dosing accuracy than gray market sources.

If you are new to peptides and waiting for legal clarity, this announcement is positive but premature. Wait for formal FDA rulemaking before assuming legal clarity has arrived.

## What to watch for next

Formal FDA notices about specific peptides being reclassified or moved off Category 2. These will appear in the Federal Register and on the FDA's compounding pages.

Changes in how 503A and 503B compounding pharmacies handle peptide preparation. The major compounding pharmacies will likely lead the way once formal rulemaking permits.

Industry reactions from manufacturers and distributors. There has been an extended period of supply chain uncertainty in the peptide market. Rules that provide clarity will likely produce more reliable supply.

State-level activity. Some states have been moving on their own to allow physician-supervised peptide therapies. These state actions can complement (or conflict with) federal policy.

## A balanced view

The 2026 announcement is meaningful. The political environment for peptides has shifted measurably. But policy follows process, and process takes time. Expect formal changes over the next 6 to 18 months rather than overnight. In the meantime, the safest pathway remains working with a licensed physician, sourcing from a reputable compounding pharmacy when available, and tracking your own response carefully.`
  },
  {
    id: "injection-site-rotation",
    title: "Injection Site Rotation: Why It Matters More Than You Think",
    excerpt: "Most peptide users get this wrong. Here's how to rotate injection sites properly to avoid lipohypertrophy, scarring, and absorption problems.",
    category: "Education",
    readTime: "5 min read",
    date: "April 21, 2026",
    color: "#16a34a",
    content: `If you are injecting peptides regularly — daily or several times per week — injection site rotation is one of the most important habits to get right. It is also the habit that most users neglect, often without realizing the consequences.

## What happens when you don't rotate

Repeatedly injecting into the same area of subcutaneous tissue can cause several problems. The most common is lipohypertrophy, which is a thickening or hardening of the fatty tissue under the skin. It feels like a firm lump and tends to persist for months even after you stop injecting that site. Beyond being unsightly, lipohypertrophy reduces the absorption of subsequent injections, meaning you effectively get a smaller dose without knowing it.

You can also develop scar tissue, persistent bruising, or in rare cases skin infections from over-used sites.

## The standard rotation pattern

For subcutaneous injections, the most common viable sites are the abdomen (avoiding the area within 2 inches of the navel), the outer thighs, the upper arms, and the love handle area.

The simple approach is to rotate through all four areas systematically. Day 1 left abdomen, day 2 right abdomen, day 3 left thigh, day 4 right thigh, day 5 left love handle, day 6 right love handle, day 7 left arm, day 8 right arm — then start over.

Within each area, also rotate within the site. If you injected the upper right quadrant of your right abdomen last week, use the lower right quadrant this week.

## Site-specific considerations

The abdomen absorbs subcutaneously administered peptides relatively quickly and consistently. It is the most popular site for most users.

The outer thighs absorb slightly more slowly and are useful for peptides where slower absorption may be desirable.

The upper arms work but can be awkward to inject yourself in. Most people need a partner or a mirror.

The love handles have a good amount of subcutaneous fat and are easy to access, but rotation within this area is important to avoid lipohypertrophy.

## What to avoid

Never inject into areas with broken skin, rashes, moles, scars, or visible blood vessels. Avoid the area within 2 inches of the navel — absorption is unreliable. Avoid the inner thighs and the inner arms — there are more blood vessels closer to the surface, increasing risk of accidentally hitting one.

## Special case: intramuscular injections

Some users prefer intramuscular injection for certain peptides (BPC-157 near an injured tendon, for example). For IM injections, common sites are the deltoid, the lateral quadriceps, and the gluteal area. The same rotation principles apply — don't hit the same exact spot repeatedly.

## What does proper rotation look like in practice?

Use a tracking tool. The TrackMyPeptide app has an injection site map that shows you exactly where you've injected in the last 7 to 30 days, so you can quickly see which areas are due for rest. If you're tracking manually, a simple grid on a piece of paper works fine — just mark which area you used each day.

## Signs you've been over-using a site

Lumps under the skin that don't go away after a few weeks. Persistent redness or bruising. Pain at the site. Reduced perceived effect of your peptide (which can indicate poor absorption from a damaged site).

If you notice any of these, give that site a complete rest for at least 4 to 6 weeks while continuing to rotate through other areas.`
  },
  {
    id: "peptide-stacks-recovery",
    title: "5 Peptide Stacks for Recovery — What Athletes Actually Use",
    excerpt: "From the Wolverine Stack to GH-driven recovery protocols, here are the five most-used stacks among serious athletes and what each one is best at.",
    category: "Recovery Protocols",
    readTime: "8 min read",
    date: "April 18, 2026",
    color: "#2d6a4f",
    content: `Recovery is the dimension where peptides have the strongest anecdotal evidence and the most consistent user reports. The community has converged on several stacks that combine peptides for synergistic effects. Here are the five most-used recovery protocols among athletes, what each is best for, and how to evaluate whether they're right for you.

## Stack 1: The Wolverine Stack (BPC-157 + TB-500)

This is the most well-known peptide stack. BPC-157 provides localized tissue repair while TB-500 provides systemic recovery support. Used for chronic tendinopathies, ligament injuries, post-surgical recovery, and stubborn joint issues.

Typical protocol: BPC-157 at 250 to 500 micrograms daily subcutaneously, ideally near the affected area. TB-500 at 2 to 2.5 mg twice per week for the first 4 to 6 weeks, then 2 to 2.5 mg every 1 to 2 weeks.

Cycle length: 6 to 12 weeks.

Best for: Chronic injuries that haven't healed with conventional rehab.

## Stack 2: The Sleep & GH Recovery Stack (Ipamorelin + CJC-1295)

Recovery happens primarily during deep sleep, when GH release is highest. This stack amplifies natural GH pulses, leading to deeper sleep, better overnight repair, and faster recovery from training.

Typical protocol: 100 to 200 micrograms of CJC-1295 (non-DAC) plus 100 to 200 micrograms of Ipamorelin, combined in one subcutaneous injection 30 to 60 minutes before bed.

Cycle length: 8 to 12 weeks on, 4 weeks off.

Best for: Athletes with high training loads who need to maximize overnight recovery.

## Stack 3: The Comprehensive Recovery Stack (BPC-157 + TB-500 + Ipamorelin + CJC-1295)

This combines the localized and systemic tissue repair of the Wolverine Stack with the GH-driven overnight recovery of the GH stack. Used by serious athletes coming back from major injuries or those with extremely high training loads.

Typical protocol: BPC-157 250 to 500 micrograms daily, TB-500 2 to 2.5 mg twice weekly, Ipamorelin and CJC-1295 combined 100 to 200 micrograms each before bed.

Cycle length: 8 to 12 weeks.

Best for: Post-surgery rehab, return-to-sport after major injury.

Caveat: Multiple peptides at once means it is harder to tell what is contributing what. Better to add one at a time when you have time.

## Stack 4: The Anti-Inflammatory Recovery Stack (BPC-157 + Thymosin Alpha-1)

Some athletes have chronic low-grade inflammation that interferes with recovery. This stack pairs BPC-157's tissue repair with Thymosin Alpha-1's immune modulation.

Typical protocol: BPC-157 250 to 500 micrograms daily. Thymosin Alpha-1 1.6 mg twice per week subcutaneously.

Cycle length: 6 to 8 weeks.

Best for: Athletes with chronic inflammation, frequent illness, or recovering from a period of overtraining.

## Stack 5: The Metabolic Recovery Stack (MOTS-c + Tesamorelin)

This is less about tissue repair and more about metabolic optimization. MOTS-c improves mitochondrial function and insulin sensitivity. Tesamorelin reduces visceral fat and increases lean mass.

Typical protocol: MOTS-c 5 to 10 mg once or twice per week. Tesamorelin 1 to 2 mg daily subcutaneously.

Cycle length: 12 to 16 weeks.

Best for: Athletes who have plateaued on body composition, masters athletes with declining metabolism, or anyone with subclinical insulin resistance.

## How to choose

Don't start with the most aggressive stack. Start with the simplest protocol that addresses your primary issue. If you have a chronic tendinopathy, try BPC-157 alone for 4 to 6 weeks before adding TB-500. If your sleep is the limiting factor, try the GH stack alone for 8 weeks before adding tissue repair peptides.

Stacking more peptides isn't better unless you can attribute results clearly to each one. The exception is when you are running a well-documented synergistic combination (like Ipamorelin + CJC-1295) that has been studied together.

## What to track

For all recovery protocols, track these variables: subjective pain or function on a 1 to 10 scale daily, sleep quality from your wearable, training load (RPE or volume), and any specific tests relevant to your injury (range of motion, single-leg balance, vertical jump, etc.).

Take baseline measurements before starting any stack. Re-measure at 4 weeks and 8 weeks. The pattern over 8 weeks tells the real story — early changes can be placebo, but consistent change over months is signal.

## Bottom line

Peptide stacks are an additive layer to good training, nutrition, sleep, and rehabilitation — not a replacement. The athletes who get the best results from peptides are the ones who already have the fundamentals dialed in. Peptides amplify the work you're doing. They don't substitute for it.`
  },
];

function Blog({ setActive }) {
  const [selectedPost, setSelectedPost] = useState(null);
  const [category, setCategory] = useState("All");
  const categories = ["All", ...new Set(BLOG_POSTS.map(p => p.category))];
  const filtered = category === "All" ? BLOG_POSTS : BLOG_POSTS.filter(p => p.category === category);

  if (selectedPost) {
    const post = BLOG_POSTS.find(p => p.id === selectedPost);
    return (
      <div style={{ padding: 40, maxWidth: 780, margin: "0 auto" }}>
        <button onClick={() => setSelectedPost(null)} style={{ background: "none", border: "none", cursor: "pointer", color: S.muted, fontSize: "0.82rem", fontWeight: 600, marginBottom: 24, display: "flex", alignItems: "center", gap: 6, padding: 0 }}>
          ← Back to Blog
        </button>

        <div style={{ marginBottom: 24 }}>
          <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}>
            <Pill color={post.color} bg={post.color + "18"}>{post.category}</Pill>
            <span style={{ fontSize: "0.78rem", color: S.muted }}>{post.readTime}</span>
            <span style={{ fontSize: "0.78rem", color: S.muted }}>·</span>
            <span style={{ fontSize: "0.78rem", color: S.muted }}>{post.date}</span>
          </div>
          <h1 style={{ fontFamily: "'Fraunces', serif", fontSize: "2.4rem", fontWeight: 900, color: S.ink, letterSpacing: "-0.03em", lineHeight: 1.1, marginBottom: 16 }}>{post.title}</h1>
          <p style={{ fontSize: "1.05rem", color: S.muted, lineHeight: 1.7, fontStyle: "italic" }}>{post.excerpt}</p>
        </div>

        <div style={{ background: S.white, border: `1px solid ${S.border}`, borderRadius: 20, padding: 48 }}>
          {post.content.split("\n\n").map((para, i) => {
            if (para.startsWith("## ")) {
              return <h2 key={i} style={{ fontFamily: "'Fraunces', serif", fontSize: "1.4rem", fontWeight: 700, color: S.ink, letterSpacing: "-0.02em", marginTop: i === 0 ? 0 : 32, marginBottom: 14 }}>{para.replace("## ", "")}</h2>;
            }
            return <p key={i} style={{ fontSize: "0.95rem", color: S.ink2, lineHeight: 1.85, marginBottom: 18 }}>{para}</p>;
          })}
        </div>

        <Card style={{ marginTop: 24, background: S.accentLight, border: `1px solid ${S.accent}33`, textAlign: "center", padding: 32 }}>
          <div style={{ fontFamily: "'Fraunces', serif", fontWeight: 700, fontSize: "1.15rem", color: S.ink, marginBottom: 8 }}>Track this protocol in the app</div>
          <p style={{ fontSize: "0.85rem", color: S.muted, marginBottom: 20, lineHeight: 1.7 }}>Use TrackMyPeptide to log doses, biomarkers, and progress for any protocol mentioned in this article.</p>
          <BtnPrimary onClick={() => setActive("protocol")}>Open Protocol Builder →</BtnPrimary>
        </Card>

        <p style={{ fontSize: "0.72rem", color: S.muted, marginTop: 24, lineHeight: 1.7, textAlign: "center" }}>
          ⚠️ Educational use only. Not medical advice. Always consult a physician before starting any peptide protocol.
        </p>
      </div>
    );
  }

  return (
    <div style={{ padding: 40 }}>
      <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: "1.8rem", fontWeight: 900, color: S.ink, letterSpacing: "-0.03em", marginBottom: 8 }}>Blog & Education</h2>
      <p style={{ color: S.muted, fontSize: "0.85rem", marginBottom: 28 }}>Research-backed peptide guides, protocol breakdowns, and FDA updates.</p>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 24 }}>
        {categories.map(c => (
          <button key={c} onClick={() => setCategory(c)} style={{ padding: "8px 16px", borderRadius: 100, border: `1.5px solid ${category === c ? S.accent : S.border}`, background: category === c ? S.accentLight : S.white, color: category === c ? S.accent : S.muted, fontSize: "0.8rem", fontWeight: 600, cursor: "pointer", fontFamily: "'Inter', sans-serif", transition: "all 0.2s" }}>
            {c}
          </button>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 18 }}>
        {filtered.map(post => (
          <div key={post.id} onClick={() => setSelectedPost(post.id)} style={{ background: S.white, border: `1px solid ${S.border}`, borderRadius: 18, padding: 26, cursor: "pointer", transition: "all 0.2s" }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = post.color + "66"; e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = "0 12px 32px rgba(0,0,0,0.06)"; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = S.border; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}>
            <div style={{ height: 4, width: 40, background: post.color, borderRadius: 2, marginBottom: 18 }} />
            <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
              <Pill color={post.color} bg={post.color + "15"}>{post.category}</Pill>
              <span style={{ fontSize: "0.7rem", color: S.muted }}>{post.readTime}</span>
            </div>
            <h3 style={{ fontFamily: "'Fraunces', serif", fontSize: "1.15rem", fontWeight: 700, color: S.ink, letterSpacing: "-0.01em", lineHeight: 1.25, marginBottom: 10 }}>{post.title}</h3>
            <p style={{ fontSize: "0.82rem", color: S.muted, lineHeight: 1.7, marginBottom: 14 }}>{post.excerpt}</p>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 14, borderTop: `1px solid ${S.border}` }}>
              <span style={{ fontSize: "0.7rem", color: S.muted, fontWeight: 500 }}>{post.date}</span>
              <span style={{ fontSize: "0.78rem", color: post.color, fontWeight: 700 }}>Read article →</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


// ─── PRICING / PACKAGES ──────────────────────────────────────

export { Blog };
